// This file is used to render the relative filter card in the filter section
// it renders all relative filter fields
// This function is called from UserFilterCard, ChartFilterGroupCard and FilterElement.

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  AnchorDatePatternRelativeFilterCollections,
  datePatternRelativeFilterCollections,
  RelativeFilterPatternCollections,
} from './FilterPatternCollection';
import { CircularProgress, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DropDownForRelativePattern, dropDownStyles } from './DropDownComponent';
import { fontSize, palette } from '../..';
import { parseISO } from 'date-fns';
import { PickerValue } from '@mui/x-date-pickers/internals';

interface IRelativeFilterCardProps {
  filterFieldData: any;
  handleCustomRequiredValueOnBlur: (
    val: number | string | Date,
    key: string,
    type?: string,
  ) => Promise<void> | void;
  handleDropDownForPatternOnChange: (event: any, key: string) => Promise<void> | void;
  cirLoading?: boolean;
  fromDate: string;
  toDate: string;
  anchorDate?: string;
  specificDate: string | Date | number;
  relativeConditionFromType?: string | number;
  relativeConditionToType?: string | number;
  from?: string;
  disabled?: boolean;
}

///Render Relative Filter Card
export const SelecRelativeFilterCard = ({
  filterFieldData,
  handleCustomRequiredValueOnBlur,
  handleDropDownForPatternOnChange,
  cirLoading = false,
  fromDate,
  toDate,
  anchorDate,
  specificDate,
  relativeConditionFromType,
  relativeConditionToType,
  from,
  disabled = false,
}: IRelativeFilterCardProps) => {
  ///Render Relative Filter Value Input Control
  const RelativeFilterValueInputControl = ({ type, exprType, ...rest }: any) => {
    return (
      <>
        <TextField
          {...rest}
          inputProps={{
            'data-testid': rest['data-testid'],
          }}
          disabled={disabled}
          InputProps={{
            style: {
              height: '25px',
              width: '100%',
              minWidth: '100%',
              fontSize: '13px',
              marginRight: '30px',
            },
          }}
          sx={{
            ...dropDownStyles.customSelect,
            minWidth: '100%',
            '& .MuiOutlinedInput-input': {
              padding: '16.5px 14px', // Consistent padding for all browsers
            },
            // Firefox-specific styling
            '@supports (-moz-appearance: none)': {
              '& .MuiOutlinedInput-input': {
                padding: '16.5px 2px 16.5px 14px',
              },
            },
          }}
          placeholder="Value"
          defaultValue={
            exprType === 'value_from'
              ? filterFieldData.current.relativeCondition?.from[1]
              : exprType === 'value_to'
                ? filterFieldData.current.relativeCondition?.to[1]
                : filterFieldData[exprType]
          }
          type={type}
          onBlur={(e) => !disabled && handleCustomRequiredValueOnBlur(e.target.value, exprType)}
        />

        {filterFieldData.isInValidData ? (
          <span className="ErrorText">Please enter valid data.</span>
        ) : null}
      </>
    );
  };

  ///Render Relative Filter Fields
  const RequiredFieldForRelativeFilter = ({ exprType, baseTestId }: any) => {
    var members = null;
    const currentValue =
      from === 'datasetFilter'
        ? filterFieldData.current.relativeCondition?.[baseTestId]?.[0]
        : filterFieldData[exprType];
    members = (
      <DropDownForRelativePattern
        items={RelativeFilterPatternCollections}
        exprType={exprType}
        handleDropDownForRelativePatternOnChange={handleDropDownForPatternOnChange}
        filterFieldData={filterFieldData}
        from={from}
        data-testid={`${baseTestId}_${currentValue?.toLowerCase()}`}
      ></DropDownForRelativePattern>
    );
    return <div style={{ width: '100%' }}>{members}</div>;
  };

  const ValueFieldForRelativeFilter = ({ exprType, baseTestId }: any) => {
    var members = null;
    members = (
      <RelativeFilterValueInputControl
        type="number"
        exprType={exprType}
        data-testid={`${baseTestId}_value_input`}
      ></RelativeFilterValueInputControl>
    );
    return <div>{members}</div>;
  };

  var membersFrom = null;
  membersFrom = (
    <DropDownForRelativePattern
      data-testid="from_unit_dropdown"
      items={datePatternRelativeFilterCollections}
      exprType={from === 'datasetFilter' ? 'from' : 'expTypeFromdate'}
      handleDropDownForRelativePatternOnChange={handleDropDownForPatternOnChange}
      filterFieldData={filterFieldData}
      from={from}
    ></DropDownForRelativePattern>
  );
  var membersTo = null;
  membersTo = (
    <DropDownForRelativePattern
      data-testid="to_unit_dropdown"
      items={datePatternRelativeFilterCollections}
      exprType={from === 'datasetFilter' ? 'to' : 'expTypeTodate'}
      handleDropDownForRelativePatternOnChange={handleDropDownForPatternOnChange}
      filterFieldData={filterFieldData}
      from={from}
    ></DropDownForRelativePattern>
  );
  var membersAnchordate = null;
  membersAnchordate = (
    <DropDownForRelativePattern
      data-testid="anchor_date_dropdown"
      items={AnchorDatePatternRelativeFilterCollections}
      exprType={from === 'datasetFilter' ? 'anchorDate' : 'expTypeAnchorDate'}
      handleDropDownForRelativePatternOnChange={handleDropDownForPatternOnChange}
      filterFieldData={filterFieldData}
      from={from}
    ></DropDownForRelativePattern>
  );
  var datemember = null;
  const parsedSpecificDate =
    specificDate instanceof Date || typeof specificDate === 'number'
      ? specificDate
      : parseISO(specificDate);
  datemember = (
    <div className="customDatePickerWidth">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          disabled={disabled}
          enableAccessibleFieldDOMStructure={false}
          value={parsedSpecificDate as PickerValue}
          onChange={(e) =>
            e &&
            !disabled &&
            handleCustomRequiredValueOnBlur(
              e,
              from === 'datasetFilter' ? 'specific_date' : 'exprInputSpecificDate',
              'date',
            )
          }
          slots={{
            textField: (params) => (
              <TextField
                {...params}
                className={`customDatePickerHeight`}
                sx={{
                  minWidth: '100%',
                  ...dropDownStyles.customSelect,
                }}
              />
            ),
          }}
        />
      </LocalizationProvider>
      {/* {filterFieldData.isInValidData ? (
        <span className="ErrorText">Please enter valid data.</span>
      ) : null} */}
    </div>
  );
  return (
    <div
      style={{
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px',
        marginTop: '-10px',
        marginLeft: from === 'datasetFilter' ? '6px' : 0,
        marginRight: from === 'datasetFilter' ? '6px' : 0,
        marginBottom: '6px',
        fontSize: '13px',
        textAlign: 'left',
        paddingBottom: '3px',
      }}
    >
      <span
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          fontSize: fontSize.medium,
          color: palette.primary.contrastText,
        }}
      >
        From{' '}
        {cirLoading ? (
          <CircularProgress size={15} />
        ) : fromDate ? (
          `(${fromDate})`
        ) : (
          '(Invalid Date)'
        )}
      </span>
      {/*To dispaly from-date after fetching*/}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <RequiredFieldForRelativeFilter
          baseTestId="from"
          exprType={from === 'datasetFilter' ? 'last_current_next_from' : 'expTypeFromRelativeDate'}
        ></RequiredFieldForRelativeFilter>
        {relativeConditionFromType !== 'current' ? (
          <ValueFieldForRelativeFilter
            baseTestId="from"
            exprType={from === 'datasetFilter' ? 'value_from' : 'exprInputFromValueType'}
          ></ValueFieldForRelativeFilter>
        ) : null}
      </div>
      {membersFrom}
      <span
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          fontSize: fontSize.medium,
          color: palette.primary.contrastText,
        }}
      >
        To {cirLoading ? <CircularProgress size={15} /> : toDate ? `(${toDate})` : '(Invalid Date)'}
      </span>{' '}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          color: palette.primary.contrastText,
        }}
      >
        <RequiredFieldForRelativeFilter
          baseTestId="to"
          exprType={from === 'datasetFilter' ? 'last_current_next_to' : 'expTypeToRelativeDate'}
        ></RequiredFieldForRelativeFilter>
        {relativeConditionToType !== 'current' ? (
          <ValueFieldForRelativeFilter
            baseTestId="to"
            exprType={from === 'datasetFilter' ? 'value_to' : 'exprInputToValueType'}
          ></ValueFieldForRelativeFilter>
        ) : null}
      </div>
      {membersTo}
      Based on Date
      {membersAnchordate}
      {anchorDate === 'specificDate' ? datemember : null}
    </div>
  );
};
